// src/lib/juliette-goto.ts
//
// Extrait les marqueurs [GOTO:/route] d'une reponse de Juliette : renvoie le texte
// nettoye (marqueurs retires, y compris un marqueur partiel pendant le streaming) +
// les routes a ouvrir dans l'ordre. Jumeau de juliette-addcal.ts. Le meme marqueur
// est retire du TTS cote serveur (normalizeForTts) pour ne jamais etre lu a voix
// haute.
//
// Hors demo une reponse porte AU PLUS un marqueur (regle du prompt). En mode demo
// (Juliette presente l'app), une meme reponse peut en porter plusieurs : chaque
// marqueur ouvre l'ecran au moment ou sa voix commence a le decrire (navigation
// par segment, cf useJulietteVoice/onSegmentStart). En mode texte, sans voix pour
// donner le rythme, on saute directement a la DERNIERE route (destination finale).
//
// POURQUOI UNE WHITELIST, et non n'importe quelle route :
// le panneau Juliette est monte dans ProtectedLayout (src/App.tsx). Naviguer vers
// une route qui n'en est PAS enfant (/pending-email/:id, /debrief/:sessionId,
// /morning-briefing/:sessionId, /onboarding, /onboarding/juliette) DEMONTE le
// panneau et coupe la session vocale en pleine conversation. Ces routes sont donc
// interdites ici. Une route inconnue est ignoree SILENCIEUSEMENT, comme le fait
// deja le cote entrant (supabase/functions/_shared/juliette-route-context.ts).

/** Routes enfants de ProtectedLayout : le panneau Juliette y survit a la navigation. */
const ALLOWED_ROUTES = new Set<string>([
  "/",
  "/prospection",
  "/prospection/terrain",
  "/prospection/cadastral",
  "/scraping",
  "/visibilite",
  "/crm",
  "/campaigns",
  "/campaigns/templates",
  "/campaigns/branding",
  "/messages",
  "/media",
  "/vitrine",
  "/referral",
  "/settings",
  "/settings/juliette",
  "/settings/juliette/ton",
  "/settings/juliette/agence",
  "/settings/juliette/documents",
  "/settings/juliette/connexions",
  "/settings/juliette/memoire",
  "/settings/juliette/confidentialite",
  "/juliette/contacts-knowledge",
  "/compliance",
]);
// Volontairement absentes bien qu'enfants du layout :
//  - /dpe, /seller-portal, /financing, /insurance : maquettes "bientot disponible"
//    affichant des donnees fictives, aucun interet d'y envoyer l'agent ;
//  - /admin/* : outils internes.

/** Fiche contact plein ecran : /crm/contact/<uuid>. */
const CONTACT_ROUTE_RE =
  /^\/crm\/contact\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Conversation d'un contact dans la messagerie : /messages?contactId=<uuid>.
 * Seul parametre de requete autorise de toute la whitelist — la page Messages
 * le resout deja (cf Messages.tsx) et c'est ce qui permet a la demo de MONTRER
 * de vrais echanges au lieu d'une inbox non selectionnee.
 */
const MESSAGES_CONTACT_ROUTE_RE =
  /^\/messages\?contactId=[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Libelles pour le bouton de repli. Fallback sur la route brute si absent. */
const ROUTE_LABELS: Record<string, string> = {
  "/": "le tableau de bord",
  "/prospection": "la prospection",
  "/prospection/terrain": "le carnet terrain",
  "/prospection/cadastral": "la prospection cadastrale",
  "/scraping": "le Sniper Web",
  "/crm": "le CRM",
  "/campaigns": "le pilotage des campagnes",
  "/campaigns/templates": "les modeles de campagnes",
  "/campaigns/branding": "le branding email",
  "/messages": "la messagerie",
  "/media": "la bibliotheque",
  "/vitrine": "le site web",
  "/referral": "les ambassadeurs",
  "/settings": "les parametres",
  "/settings/juliette": "le centre de controle de Juliette",
  "/settings/juliette/ton": "les reglages de ton",
  "/settings/juliette/agence": "la memoire agence",
  "/settings/juliette/documents": "les documents de Juliette",
  "/settings/juliette/connexions": "les connexions",
  "/settings/juliette/memoire": "la memoire de Juliette",
  "/settings/juliette/confidentialite": "les donnees et la confidentialite",
  "/juliette/contacts-knowledge": "ce que Juliette sait des contacts",
  "/compliance": "la conformite",
};

export function isNavigableRoute(route: string): boolean {
  return (
    ALLOWED_ROUTES.has(route) ||
    CONTACT_ROUTE_RE.test(route) ||
    MESSAGES_CONTACT_ROUTE_RE.test(route)
  );
}

export function routeLabel(route: string): string {
  if (ROUTE_LABELS[route]) return ROUTE_LABELS[route];
  if (CONTACT_ROUTE_RE.test(route)) return "la fiche du contact";
  if (MESSAGES_CONTACT_ROUTE_RE.test(route)) return "la conversation du contact";
  return "cette page";
}

// `?` et `=` uniquement pour /messages?contactId=<uuid> — la validation stricte
// reste dans isNavigableRoute, le regex ne fait qu'extraire le candidat.
const GOTO_RE_GLOBAL = /\[GOTO:(\/[A-Za-z0-9/_?=-]*)\]/gi;

/**
 * Retire TOUS les marqueurs du texte affiche et renvoie les routes navigables
 * dans l'ordre d'apparition. Un marqueur est toujours retire, meme quand sa route
 * est refusee : l'agent ne doit jamais voir "[GOTO:...]" dans une bulle.
 */
export function extractAllGotos(content: string): { text: string; routes: string[] } {
  if (!content) return { text: content, routes: [] };
  const routes: string[] = [];
  const text = content
    .replace(GOTO_RE_GLOBAL, (_marker, raw: string) => {
      // Normalise un slash final ("/crm/" -> "/crm"), sauf pour la racine.
      const route = raw.length > 1 ? raw.replace(/\/+$/, "") : raw;
      if (isNavigableRoute(route)) routes.push(route);
      return "";
    })
    .replace(/\[GOTO:[^\]]*$/i, "") // marqueur incomplet en cours de streaming
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
  return { text, routes };
}

/** Variante mono-route : la premiere route navigable (cas hors demo). */
export function extractGoto(content: string): { text: string; route: string | null } {
  const { text, routes } = extractAllGotos(content);
  return { text, route: routes[0] ?? null };
}
